import { useCallback, useEffect, useState } from "react";
import {
    Plus,
    Pencil,
    Trash2,
    ChevronUp,
    ChevronDown,
    Power,
    Loader2,
    ClipboardCheck,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";
import { EmptyState } from "@/components/common/EmptyState";
import QCSectionFormDialog from "./QCSectionFormDialog";

export default function QCInspectionSectionsAdmin() {
    const { t, lang } = useI18n();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/qc-inspection/sections/all");
            setRows(r.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        load();
    }, [load]);

    const withBusy = async (id, fn) => {
        setBusyId(id);
        try {
            await fn();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setBusyId(null);
        }
    };

    const move = (id, direction) =>
        withBusy(id, async () => {
            await api.post(`/qc-inspection/sections/${id}/move`, { direction });
            await load();
        });

    const toggle = (id) =>
        withBusy(id, async () => {
            await api.post(`/qc-inspection/sections/${id}/toggle`);
            await load();
        });

    const openNew = () => {
        setEditing(null);
        setFormOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setFormOpen(true);
    };

    const confirmDelete = () =>
        withBusy(pendingDelete?.id, async () => {
            await api.delete(`/qc-inspection/sections/${pendingDelete.id}`);
            toast.success(t("qc_ins_deleted"));
            setPendingDelete(null);
            await load();
        });

    return (
        <div data-testid={OPS.qcIns.adminPage} className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-base font-bold text-[color:var(--ink)]">{t("qc_ins_admin_title")}</h2>
                    <p className="text-xs text-[color:var(--ink-muted)] max-w-2xl">{t("qc_ins_admin_desc")}</p>
                </div>
                <Button
                    data-testid={OPS.qcIns.addBtn}
                    onClick={openNew}
                    className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                >
                    <Plus className="h-4 w-4 me-2" />
                    {t("qc_ins_add_section")}
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-[color:var(--brand-orange)]" />
                </div>
            ) : rows.length === 0 ? (
                <EmptyState
                    icon={ClipboardCheck}
                    title={t("qc_ins_no_sections")}
                    hint={t("qc_ins_no_sections_hint")}
                    testId={OPS.qcIns.emptyState}
                />
            ) : (
                <div className="space-y-3">
                    {rows.map((row, idx) => (
                        <SectionCard
                            key={row.id}
                            row={row}
                            lang={lang}
                            t={t}
                            isFirst={idx === 0}
                            isLast={idx === rows.length - 1}
                            busy={busyId === row.id}
                            onEdit={() => openEdit(row)}
                            onDelete={() => setPendingDelete(row)}
                            onToggle={() => toggle(row.id)}
                            onMoveUp={() => move(row.id, "up")}
                            onMoveDown={() => move(row.id, "down")}
                        />
                    ))}
                </div>
            )}

            <QCSectionFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                section={editing}
                onSaved={async () => {
                    toast.success(t("qc_ins_saved"));
                    await load();
                }}
            />

            <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("qc_ins_delete_confirm")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("qc_ins_delete_confirm_desc")}
                            {pendingDelete ? (
                                <span className="mt-2 block font-medium text-[color:var(--ink)]">
                                    {lang === "ar" ? pendingDelete.title_ar : (pendingDelete.title_en || pendingDelete.title_ar)}
                                </span>
                            ) : null}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={OPS.qcIns.deleteCancel}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid={OPS.qcIns.deleteConfirm}
                            onClick={confirmDelete}
                            className="bg-[color:var(--danger)] text-white hover:bg-[color:var(--danger)]/90"
                        >
                            {t("qc_ins_delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function SectionCard({ row, lang, t, isFirst, isLast, busy, onEdit, onDelete, onToggle, onMoveUp, onMoveDown }) {
    const title = lang === "ar" ? row.title_ar : (row.title_en || row.title_ar);
    const subtitle = lang === "ar" ? row.subtitle_ar : (row.subtitle_en || row.subtitle_ar);

    return (
        <Card
            data-testid={OPS.qcIns.card(row.id)}
            className={`surface border-[color:var(--border-soft)] rounded-xl shadow-[var(--shadow-sm)] transition-opacity ${
                row.is_active ? "" : "opacity-70"
            }`}
        >
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="text-sm font-bold text-[color:var(--ink)]">{title}</h3>
                            <Badge
                                variant="secondary"
                                className={
                                    row.is_active
                                        ? "bg-[color:rgba(46,160,67,0.14)] text-[color:var(--success)] border-0 text-[10px]"
                                        : "bg-[color:rgba(151,138,120,0.22)] text-[color:var(--ink-muted)] border-0 text-[10px]"
                                }
                            >
                                {row.is_active ? t("qc_ins_active") : t("qc_ins_inactive")}
                            </Badge>
                            <span className="text-[10px] text-[color:var(--ink-muted)] font-mono">
                                {row.section_key}
                            </span>
                        </div>
                        {subtitle ? (
                            <p className="text-xs text-[color:var(--ink-muted)]">{subtitle}</p>
                        ) : null}
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                        <IconButton
                            testId={OPS.qcIns.moveUpBtn(row.id)}
                            aria={t("qc_ins_move_up")}
                            disabled={isFirst || busy}
                            onClick={onMoveUp}
                        >
                            <ChevronUp className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                            testId={OPS.qcIns.moveDownBtn(row.id)}
                            aria={t("qc_ins_move_down")}
                            disabled={isLast || busy}
                            onClick={onMoveDown}
                        >
                            <ChevronDown className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                            testId={OPS.qcIns.toggleBtn(row.id)}
                            aria={t("qc_ins_toggle")}
                            disabled={busy}
                            onClick={onToggle}
                        >
                            <Power className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                            testId={OPS.qcIns.editBtn(row.id)}
                            aria={t("qc_ins_edit")}
                            disabled={busy}
                            onClick={onEdit}
                        >
                            <Pencil className="h-4 w-4" />
                        </IconButton>
                        <IconButton
                            testId={OPS.qcIns.deleteBtn(row.id)}
                            aria={t("qc_ins_delete")}
                            disabled={busy}
                            onClick={onDelete}
                            danger
                        >
                            <Trash2 className="h-4 w-4" />
                        </IconButton>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="pt-0">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold text-[color:var(--ink-muted)] uppercase tracking-wide">
                        {t("qc_ins_items")}
                    </span>
                    <span className="text-[11px] text-[color:var(--ink-muted)]">
                        {t("qc_ins_items_count")(row.items.length)}
                    </span>
                </div>
                {row.items.length === 0 ? (
                    <p className="text-xs text-[color:var(--ink-muted)] italic">—</p>
                ) : (
                    <ol className="space-y-1 list-decimal list-inside text-sm text-[color:var(--ink)]">
                        {row.items.map((it, i) => (
                            <li key={i} className="ps-1">
                                <bdi>{it}</bdi>
                            </li>
                        ))}
                    </ol>
                )}
            </CardContent>
        </Card>
    );
}

function IconButton({ children, testId, aria, disabled, onClick, danger }) {
    return (
        <Button
            data-testid={testId}
            aria-label={aria}
            variant="ghost"
            size="icon"
            className={`h-8 w-8 ${
                danger
                    ? "text-[color:var(--danger)] hover:text-[color:var(--danger)] hover:bg-[color:rgba(255,69,58,0.10)]"
                    : "text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
            }`}
            disabled={disabled}
            onClick={onClick}
        >
            {children}
        </Button>
    );
}
